import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import AppShell from "@/components/AppShell";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Kalender from "@/pages/Kalender";
import Kart from "@/pages/Kart";
import Maskiner from "@/pages/Maskiner";
import MaskinDetalj from "@/pages/MaskinDetalj";
import Kunder from "@/pages/Kunder";
import Deler from "@/pages/Deler";
import Rapportar from "@/pages/Rapportar";
import Huskeliste from "@/pages/Huskeliste";
import NyServicerapport from "@/pages/NyServicerapport";
import NyJobbrapport from "@/pages/NyJobbrapport";
import Brukarar from "@/pages/Brukarar";
import Innstillingar from "@/pages/Innstillingar";
import Profil from "@/pages/Profil";

const FullLoader = () => (
  <div className="flex min-h-screen items-center justify-center bg-background">
    <Loader2 className="h-8 w-8 animate-spin text-primary" />
  </div>
);

function Protected({ children, adminOnly }) {
  const { user, loading } = useAuth();
  if (loading) return <FullLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/" replace />;
  return children;
}

/** Module behind a feature flag (built, but hidden in pilot) -> redirect when switched off. */
function Feature({ flag, fallback = "/", children }) {
  const { features } = useAuth();
  if (!features?.[flag]) return <Navigate to={fallback} replace />;
  return children;
}

// Keep old bookmarks working: /servicerapporter -> /rapportar
function LegacyRedirect({ to }) {
  const { search } = useLocation();
  return <Navigate to={`${to}${search}`} replace />;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route
            element={
              <Protected>
                <AppShell />
              </Protected>
            }
          >
            <Route path="/" element={<Dashboard />} />
            <Route path="/kalender" element={<Kalender />} />
            <Route path="/kart" element={<Feature flag="map_enabled"><Kart /></Feature>} />
            <Route path="/maskiner" element={<Maskiner />} />
            <Route path="/maskiner/:id" element={<MaskinDetalj />} />
            <Route path="/kunder" element={<Kunder />} />
            <Route path="/deler" element={<Feature flag="parts_page_enabled" fallback="/maskiner"><Deler /></Feature>} />
            <Route path="/rapportar" element={<Rapportar />} />
            <Route path="/rapportar/ny-service" element={<NyServicerapport />} />
            <Route path="/rapportar/ny-jobb" element={<NyJobbrapport />} />
            <Route path="/huskeliste" element={<Huskeliste />} />
            <Route path="/profil" element={<Profil />} />
            <Route path="/servicerapporter" element={<LegacyRedirect to="/rapportar" />} />
            <Route path="/servicerapporter/ny" element={<LegacyRedirect to="/rapportar/ny-service" />} />
            <Route
              path="/brukarar"
              element={
                <Protected adminOnly>
                  <Brukarar />
                </Protected>
              }
            />
            <Route
              path="/innstillingar"
              element={
                <Protected adminOnly>
                  <Innstillingar />
                </Protected>
              }
            />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" closeButton toastOptions={{ classNames: { toast: "font-body" } }} />
    </AuthProvider>
  );
}

export default App;
